import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PricingReport } from 'types/pricingSimulator';
import { HookInputProps } from 'types/hooks';
import { getPercentageChange, getPercentageChangeV2 } from 'utils/helperMethods';
const errorMessage: string = 'Something went wrong!';

// api with parser logic
interface FetchGlobalKPIsDataProp extends HookInputProps {
    updateReportProperty: <K extends keyof PricingReport>(key: K, value: PricingReport[K]) => void;
    selectedReportInfo: PricingReport | null;
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    count: number;
    setData: Function;
}

function useFetchGlobalKPIsData({
    updateReportProperty,
    selectedReportInfo,
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
}: FetchGlobalKPIsDataProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData = getParsedGlobalKpiData(payload);

            if (parsedData?.length > 0 && selectedReportInfo) {
                updateReportProperty('baseKPIs', {
                    ...selectedReportInfo?.baseKPIs,
                    kpiTiles: parsedData,
                });
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
        setData,
    };
    return outputData;
}

export { useFetchGlobalKPIsData };

export const getParsedGlobalKpiData = (data: any[]) => {
    const result = data?.map((k: any) => {
        return {
            label: k.kpi_name,
            id: k.kpi_id,
            newValue: k.new,
            oldValue: k.actual,
            absChange: k.new - k.actual,
            percentChange: getPercentageChangeV2(k.actual, k.new),
        };
    });

    result.sort((a, b) => a.id - b.id);

    return result;
};
